interface SSELogoutConfig {
  url: string;
  onLogout: () => void;
  queryParams?: Record<string, string>;
  customHeaders?: Record<string, string>;
  getToken: () => Promise<string | null>;
  debug?: boolean;
}

class SSELogoutUtility {
  private abortController: AbortController | null = null;
  private isConnected: boolean = false;
  private config: SSELogoutConfig | null = null;
  private reconnectAttempts: number = 0;
  // Caps the exponential backoff, not a give-up threshold - reconnects keep
  // retrying every maxBackoffMs indefinitely while a tab is authenticated,
  // otherwise a dropped connection (laptop sleep, brief network loss, VPN
  // switch) leaves that tab permanently deaf to cross-app logout events.
  private readonly maxBackoffAttempts: number = 3;
  private readonly maxBackoffMs: number = 30000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private recoveryListenersRegistered: boolean = false;

  private log(message: string, ...args: any[]) {
    if (this.config?.debug) {
      console.log(`[SSE Logout] ${message}`, ...args);
    }
  }

  private error(message: string, ...args: any[]) {
    console.error(`[SSE Logout] ${message}`, ...args);
  }

  async connect(config: SSELogoutConfig): Promise<void> {
    this.config = config;
    this.registerRecoveryListeners();

    if (this.abortController) {
      this.disconnect();
    }

    try {
      const token = await config.getToken();

      if (!token) {
        this.error('No authentication token available');
        return;
      }

      const urlObj = new URL(config.url);
      if (config.queryParams) {
        Object.entries(config.queryParams).forEach(([key, value]) => {
          urlObj.searchParams.append(key, value);
        });
      }

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      };

      if (config.customHeaders) {
        Object.assign(headers, config.customHeaders);
      }

      this.log('Connecting to:', urlObj.toString());

      this.abortController = new AbortController();

      const response = await fetch(urlObj.toString(), {
        method: 'GET',
        headers,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.log('Connection established successfully');

      await this.processStream(response.body);

    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        this.error('Failed to establish connection:', err);
        this.handleReconnect();
      }
    }
  }

  private async processStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          this.log('Stream ended');
          this.handleReconnect();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine === '' || !this.processLine(trimmedLine)) continue;
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        this.error('Stream processing error:', err);
        this.handleReconnect();
      }
    } finally {
      reader.releaseLock();
      this.isConnected = false;
    }
  }

  private processLine(line: string): boolean {
    this.log('Processing line:', `"${line}"`);

    // Handle data lines
    if (line.startsWith('data:')) {
      const data = line.startsWith('data: ')
        ? line.substring(6).trim()
        : line.substring(5).trim();

      this.log('Data extracted:', `"${data}"`);

      if (this.isLogoutMessage(data)) {
        this.log('🚨 LOGOUT MESSAGE DETECTED! Triggering logout...');
        this.triggerLogout();
        return false; // Stop processing
      }
    }
    // Handle event types
    else if (line.startsWith('event:')) {
      const eventType = line.startsWith('event: ')
        ? line.substring(7).trim()
        : line.substring(6).trim();

      this.log('Event type:', eventType);

      if (this.isLogoutMessage(eventType)) {
        this.log('🚨 LOGOUT EVENT DETECTED! Triggering logout...');
        this.triggerLogout();
        return false; // Stop processing
      }
    }
    // Handle raw logout messages
    else if (this.isLogoutMessage(line)) {
      this.log('🚨 RAW LOGOUT MESSAGE DETECTED! Triggering logout...');
      this.triggerLogout();
      return false; // Stop processing
    }

    return true; // Continue processing
  }

  private isLogoutMessage(message: string): boolean {
    if (!message) return false;

    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage === 'logout' ||
      lowerMessage.includes('logout') ||
      message === 'LOGOUT' ||
      lowerMessage === 'signout' ||
      lowerMessage.includes('signout')
    );
  }

  private triggerLogout(): void {
    this.disconnect();
    this.config?.onLogout();
  }

  private handleReconnect(): void {
    if (!this.config) return;

    this.reconnectAttempts++;
    // Backoff grows until maxBackoffAttempts, then holds at maxBackoffMs and
    // keeps retrying rather than giving up permanently.
    const delay = Math.min(
      1000 * Math.pow(2, Math.min(this.reconnectAttempts, this.maxBackoffAttempts) - 1),
      this.maxBackoffMs,
    );

    this.log(`Attempting reconnection ${this.reconnectAttempts} in ${delay}ms`);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      if (this.config) {
        this.connect(this.config);
      }
    }, delay);
  }

  /**
   * Reconnect immediately (bypassing backoff) when the browser regains
   * connectivity or the tab becomes visible again, instead of waiting for
   * the next scheduled retry - covers laptop sleep / VPN switches / network
   * blips that would otherwise leave a tab deaf to logout events for a while.
   */
  private registerRecoveryListeners(): void {
    if (this.recoveryListenersRegistered || typeof window === 'undefined') return;
    this.recoveryListenersRegistered = true;

    const reconnectNow = () => {
      if (this.isConnected || !this.config) return;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.reconnectAttempts = 0;
      this.connect(this.config);
    };

    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reconnectNow();
    });
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.isConnected = false;
      this.log('Connection disconnected');
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }
}

// Singleton instance for global use
const sseLogout = new SSELogoutUtility();

// Export singleton for direct usage
export default sseLogout;
